import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, defineMessages } from 'react-intl';
import { withSiteData } from 'react-static';

import Chip from '../brand/Chip';
import Hyperlink from '../common/Hyperlink';
import {
  Section,
  Inner,
  SectionTitle,
  Cover,
  HeadlineRow,
  ViewAllLink,
  scrollRow,
  imageHover,
} from './shared';
import { media } from '../../styles/style-utils';
import { type } from '../../styles/typography';
import schiff from '../../assets/images/home/chart-schiff.jpg';
import perahia from '../../assets/images/home/chart-perahia.jpg';
import nguci from '../../assets/images/home/chart-nguci.jpg';

const messages = defineMessages({
  conductorSoloist: {
    id: 'home.charts.role.conductor-soloist',
    defaultMessage: 'Conductor, Soloist',
  },
  soloist: { id: 'home.charts.role.soloist', defaultMessage: 'Soloist' },
});

const ENTRIES = [
  {
    rank: 1,
    image: schiff,
    name: 'Sir András Schiff',
    role: messages.conductorSoloist,
    slug: 'andras-schiff',
  },
  {
    rank: 2,
    image: perahia,
    name: 'Murray Perahia',
    role: messages.conductorSoloist,
    slug: 'murray-perahia',
  },
  {
    rank: 3,
    image: nguci,
    name: 'Marie-Ange Nguci',
    role: messages.soloist,
    slug: 'marieange-nguci',
  },
];

const Wrapper = styled(Section)`
  padding-top: 64px;
  padding-bottom: 72px;
  background-color: var(--b-white);
  color: var(--b-black);

  ${media.large`
    padding-top: 92px;
    padding-bottom: 100px;
  `};
`;

const Content = styled(Inner)`
  display: flex;
  flex-direction: column;
  gap: 48px;

  ${media.large`
    gap: 102px;
  `};
`;

const Track = styled.div`
  ${scrollRow};
  gap: 16px;
  align-items: flex-end;

  ${media.large`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  `};
`;

const Card = styled(Hyperlink)`
  position: relative;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  width: 320px;
  border-radius: var(--b-radius);
  background-color: #f1f1f3;
  color: var(--b-black);
  text-decoration: none !important;
  scroll-snap-align: start;
  transition: background-color 0.25s ease;
  ${imageHover};

  &:hover {
    background-color: var(--b-smoke);
  }

  &:hover .image span {
    color: var(--b-white);
  }

  ${media.large`
    width: auto;
  `};

  .top {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
    min-height: 206px;
    padding: 32px 20px;
  }

  .meta {
    width: 100%;
    padding-right: 72px;
    white-space: nowrap;

    h3 {
      ${type('headline', 'small')};
      margin: 0;
      padding: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      color: var(--b-black);
    }

    p {
      ${type('body', 'large')};
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      color: rgba(0, 0, 0, 0.6);
    }
  }

  .indicator {
    position: absolute;
    top: 41px;
    right: 20px;
    display: flex;
    align-items: center;
    gap: 4px;

    i {
      display: block;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background-color: ${({ dot }) => dot || 'var(--b-lavender-grey)'};
    }

    img {
      display: block;
      width: 34px;
      height: 18px;
    }
  }

  .image {
    position: relative;
    height: 220px;
    border-radius: var(--b-radius);
    overflow: hidden;
    background-color: #b4bebd;

    ${media.large`
      height: 292px;
    `};

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.52) 100%);
    }

    span {
      ${type('body', 'small', { uppercase: true })};
      position: absolute;
      left: 10px;
      bottom: 10px;
      z-index: 1;
      color: rgba(255, 255, 255, 0.6);
      white-space: nowrap;
      transition: color 0.25s ease;
    }
  }
`;

const Charts = ({ appDomain }) => (
  <Wrapper>
    <Content>
      <HeadlineRow>
        <SectionTitle>
          <FormattedMessage
            id="home.charts.headline"
            defaultMessage="What People Listen to Most. {br}Discover the IDAGIO Charts."
            values={{ br: <br /> }}
          />
        </SectionTitle>
        <ViewAllLink to="/magazine/idagio-classical-charts-2026-09" openInNewTab />
      </HeadlineRow>
      <Track>
        {ENTRIES.map(({ rank, image, name, role, slug }) => (
          <Card key={rank} to={`${appDomain}/profiles/${slug}`} openInNewTab>
            <div className="top">
              <Chip tone="white" uppercase>
                <FormattedMessage
                  id="home.charts.rank"
                  defaultMessage="No. {rank} Soloist"
                  values={{ rank }}
                />
              </Chip>
              <div className="meta">
                <h3>{name}</h3>
                <p>
                  <FormattedMessage {...role} />
                </p>
              </div>
            </div>
            <div className="image">
              <Cover src={image} alt="" />
              <span>
                <FormattedMessage id="home.charts.view-more" defaultMessage="View more" />
              </span>
            </div>
          </Card>
        ))}
      </Track>
    </Content>
  </Wrapper>
);

Charts.propTypes = {
  appDomain: PropTypes.string.isRequired,
};

export default withSiteData(Charts);



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/home/Charts.js